import React, {useState, useEffect, useRef} from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    MenuItem,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Alert,
} from '@mui/material';
import {toast} from 'react-toastify';
import {buildApi} from 'utils/api';
import {useAuth} from 'context/AuthContext';
import {resolveBuildMode, getBuildModeLabel} from 'utils/buildMode';

const styles = {
    paper: {
        p: {xs: 2, md: 3},
        mb: 3,
    },
    title: {
        mb: 2,
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    row: {
        display: 'flex',
        flexDirection: {xs: 'column', md: 'row'},
        gap: 2,
        '& > *': {
            flex: 1,
        },
    },
    selectWithLoading: {
        position: 'relative',
    },
    loadingIndicator: {
        position: 'absolute',
        right: 40,
        top: '50%',
        transform: 'translateY(-50%)',
    },
    stageBox: {
        display: 'flex',
        alignItems: 'center',
    },
    modeAlert: {
        py: 0,
        alignItems: 'center',
    },
    deployBox: {
        m: 0,
        px: 1.5,
        pb: 0.5,
        border: 1,
        borderColor: 'rgba(255, 255, 255, 0.23)',
        borderRadius: 1,
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 3,
    },
    deployLegend: {
        px: 0.5,
        fontSize: '0.75rem',
        color: 'text.secondary',
    },
};

export default function BuildForm({onBuildCreated}) {
    const {isAdmin} = useAuth();
    const [products, setProducts] = useState([]);
    const [versions, setVersions] = useState([]);
    // «배포» 체크박스 — 제품 저장소의 deploy-targets.json 에서 고른 버전에 맞는 항목만 (관리자만 · 없으면 영역 자체가 안 보임)
    const [deployOptions, setDeployOptions] = useState([]);
    const [deployChecked, setDeployChecked] = useState([]);
    const deployRequestRef = useRef(0);
    // 제품을 고를 때 받아 둔 모든 버전의 배포 대상 {"type:name": [...]} — 버전을 고르면 기다리지 않고 바로 보여 준다
    const [deployTargetsByRef, setDeployTargetsByRef] = useState({productId: null, targets: {}});
    const [profiles, setProfiles] = useState([]);

    const [productId, setProductId] = useState('');
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [profile, setProfile] = useState('');
    const [isStage, setIsStage] = useState(false);

    const [loadingOptions, setLoadingOptions] = useState(true);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // 현재 로딩 중인 제품 ID를 추적하여 race condition 방지
    const loadingProductIdRef = useRef(null);

    useEffect(() => {
        loadOptions();
        // early loading: 최근 빌드된 제품들의 git ref를 미리 조회해 캐시 적재 (UI 비차단)
        buildApi.prefetchRecent().catch(() => {});
    }, []);

    useEffect(() => {
        if (productId) {
            loadVersionsAndProfiles(productId);
        } else {
            // 제품 선택 해제 시 즉시 초기화
            loadingProductIdRef.current = null;
            setVersions([]);
            setProfiles([]);
            setSelectedVersion(null);
            setProfile('');
            setIsStage(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    // 제품을 고르면 버전 목록과 함께 모든 버전의 배포 대상을 미리 받아 둔다 (관리자만 · 실패하면 버전별 조회로 대신한다)
    useEffect(() => {
        setDeployTargetsByRef({productId: null, targets: {}});
        if (!isAdmin || !productId) {
            return;
        }
        let cancelled = false;
        buildApi.getAllDeployTargets(productId)
            .then(({data}) => {
                if (!cancelled) {
                    setDeployTargetsByRef({productId, targets: data.targets || {}});
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [isAdmin, productId]);

    const selectedDeployKey = selectedVersion ? `${selectedVersion.type}:${selectedVersion.name}` : null;
    const prefetchedDeployTargets = deployTargetsByRef.productId === productId && selectedDeployKey
        ? deployTargetsByRef.targets[selectedDeployKey]
        : undefined;

    // 버전을 고르면 그 버전의 배포 대상을 보여 준다. 처음엔 모두 해제 — 직접 체크한 곳에만 배포한다.
    // 미리 받아 둔 목록에 있으면 바로 쓰고, 없으면(방금 만든 태그 등) 그 버전만 서버에 묻는다.
    useEffect(() => {
        setDeployOptions([]);
        setDeployChecked([]);
        if (!isAdmin || !productId || !selectedVersion) {
            return;
        }
        const requestId = ++deployRequestRef.current;
        if (prefetchedDeployTargets) {
            setDeployOptions(prefetchedDeployTargets);
            return;
        }
        buildApi.getDeployTargets(productId, selectedVersion.type, selectedVersion.name)
            .then(({data}) => {
                if (deployRequestRef.current === requestId) {
                    setDeployOptions(data.targets || []);
                }
            })
            .catch(() => {
                // 읽지 못하면 배포 영역을 보이지 않는다 — 빌드는 그대로 요청할 수 있다.
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, productId, selectedVersion]);

    // 버전을 먼저 고르고 미리 받기가 뒤에 끝났으면, 그때 채운다 (이미 체크한 것은 그대로 둔다)
    useEffect(() => {
        if (prefetchedDeployTargets) {
            deployRequestRef.current++;
            setDeployOptions(prefetchedDeployTargets);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deployTargetsByRef]);

    const handleDeployToggle = (id) => {
        setDeployChecked(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };

    const loadOptions = async () => {
        try {
            const {data} = await buildApi.getOptions();
            setProducts(data.products || []);
        } catch (err) {
            toast.error('옵션 로드 실패');
        } finally {
            setLoadingOptions(false);
        }
    };

    const loadVersionsAndProfiles = async (pid) => {
        // 현재 로딩 대상 제품 ID 저장
        loadingProductIdRef.current = pid;

        // 즉시 이전 데이터 초기화
        setVersions([]);
        setProfiles([]);
        setSelectedVersion(null);
        setProfile('');
        setIsStage(false);
        setLoadingVersions(true);

        try {
            const {data} = await buildApi.getVersions(pid);

            // 응답이 도착했을 때 여전히 해당 제품이 선택되어 있는지 확인
            if (loadingProductIdRef.current !== pid) {
                return; // 다른 제품으로 변경됨, 결과 무시
            }

            setVersions(data.versions || []);

            const product = products.find(p => p.id === pid);
            setProfiles(product?.profiles || []);
        } catch (err) {
            // 여전히 해당 제품이 선택되어 있는 경우에만 에러 표시
            if (loadingProductIdRef.current === pid) {
                toast.error('버전 로드 실패');
                setVersions([]);
            }
        } finally {
            // 여전히 해당 제품이 선택되어 있는 경우에만 로딩 상태 해제
            if (loadingProductIdRef.current === pid) {
                setLoadingVersions(false);
            }
        }
    };

    const handleVersionChange = (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedVersion(null);
            setIsStage(false);
            return;
        }
        // value는 "type:name" 형식
        const [type, ...nameParts] = value.split(':');
        const name = nameParts.join(':');
        const version = versions.find(v => v.type === type && v.name === name);
        setSelectedVersion(version || null);
        // 태그(정식) 선택 시 스테이지 구분은 의미가 없으므로 해제
        if (version?.type !== 'branch') {
            setIsStage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!productId) {
            toast.error('제품을 선택하세요.');
            return;
        }
        if (!selectedVersion) {
            toast.error('버전을 선택하세요.');
            return;
        }

        const mode = resolveBuildMode(selectedVersion, isStage);

        // 브랜치/버전 결정: 태그 선택 시 태그명을 branch로 전달
        const branch = selectedVersion.name;
        const version = selectedVersion.version;

        setSubmitting(true);
        try {
            const {data} = await buildApi.createBuild({
                productId,
                branch,
                version,
                profile,
                mode,
                // 배포 영역이 보였을 때만 보낸다(빈 배열 = 배포 안 함). 안 보였으면 보내지 않아 빌드 스크립트의 기본 규칙을 따른다.
                ...(deployOptions.length > 0 ? {deployTargets: profile ? [] : deployChecked} : {}),
            });
            toast.success('빌드가 요청되었습니다.');
            onBuildCreated?.(data.build);

            setProductId('');
            setSelectedVersion(null);
            setProfile('');
            setIsStage(false);
        } catch (err) {
            toast.error(err.response?.data?.error || '빌드 요청 실패');
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingOptions) {
        return (
            <Paper sx={styles.paper}>
                <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                    <CircularProgress size={32}/>
                </Box>
            </Paper>
        );
    }

    const isBranch = selectedVersion?.type === 'branch';
    const mode = resolveBuildMode(selectedVersion, isStage);

    return (
        <Paper sx={styles.paper}>
            <Typography variant="h2" sx={styles.title}>
                새 빌드 요청
            </Typography>

            <Box component="form"
                 onSubmit={handleSubmit}
                 sx={styles.form}>
                <Box sx={styles.row}>
                    <TextField
                        select
                        label="제품 *"
                        value={productId}
                        onChange={(e) => setProductId(e.target.value)}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="">제품 선택</MenuItem>
                        {products.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                                {p.name} {p.description && `(${p.description})`}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Box sx={styles.selectWithLoading}>
                        <TextField
                            select
                            label="버전 *"
                            value={selectedVersion ? `${selectedVersion.type}:${selectedVersion.name}` : ''}
                            onChange={handleVersionChange}
                            size="small"
                            fullWidth
                            disabled={!productId || loadingVersions}
                        >
                            <MenuItem value="">
                                {!productId ? '제품을 먼저 선택하세요' : loadingVersions ? '로딩 중...' : '버전 선택'}
                            </MenuItem>
                            {versions.map((v) => (
                                <MenuItem key={`${v.type}:${v.name}`} value={`${v.type}:${v.name}`}>
                                    {v.type === 'branch' ? v.name : v.version}
                                </MenuItem>
                            ))}
                        </TextField>
                        {loadingVersions && (
                            <CircularProgress size={20} sx={styles.loadingIndicator}/>
                        )}
                    </Box>
                </Box>

                <Box sx={styles.row}>
                    <TextField
                        select
                        label="프로파일"
                        value={profile}
                        onChange={(e) => setProfile(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!productId}
                    >
                        <MenuItem value="">
                            {!productId ? '제품을 먼저 선택하세요' : '선택 안함'}
                        </MenuItem>
                        {profiles.map((p) => (
                            <MenuItem key={p.id} value={p.name}>
                                {p.name} {p.description && `(${p.description})`}
                            </MenuItem>
                        ))}
                    </TextField>
                    {/* 모드는 버전 종류와 스테이지 체크 여부로 자동 결정됨 */}
                    <Box sx={styles.stageBox}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isStage}
                                    onChange={(e) => setIsStage(e.target.checked)}
                                    disabled={!isBranch}
                                    size="small"
                                />
                            }
                            label="스테이지"
                        />
                    </Box>
                </Box>

                {deployOptions.length > 0 && (
                    <Box component="fieldset" sx={styles.deployBox}>
                        <Typography component="legend" sx={styles.deployLegend}>배포</Typography>
                        {deployOptions.map((t) => (
                            <FormControlLabel
                                key={t.id}
                                control={
                                    <Checkbox
                                        checked={!profile && deployChecked.includes(t.id)}
                                        onChange={() => handleDeployToggle(t.id)}
                                        disabled={!!profile}
                                        size="small"
                                    />
                                }
                                label={t.label}
                            />
                        ))}
                    </Box>
                )}

                <Alert severity={mode ? 'info' : 'warning'} sx={styles.modeAlert}>
                    {mode
                        ? `이 빌드는 "${getBuildModeLabel(mode)}" 빌드로 요청됩니다.`
                        : '버전을 선택하면 빌드 구분이 표시됩니다.'}
                </Alert>

                <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{alignSelf: 'flex-start', mt: 1}}
                >
                    {submitting ? '요청 중...' : '빌드 시작'}
                </Button>
            </Box>
        </Paper>
    );
}
